import React, { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useSlerp } from '@slerp/client'
import { QUERY_LOYALTY_CARDS } from 'components/Loyalty/LoyaltyQueries'
import { useLoyaltyCards } from 'hooks/useGlobalContext/useLoyaltyCards'
import { LoaderProps } from './types'

// Fetches loyalty cards once for the whole Loyalty section, so descendants
// (the stamp modal, bulk-add drawer) don't each fire their own
// getLoyaltyCards request. Mounted in LoyaltyWrapper.tsx above the Loyalty
// tabs so it also survives switching tabs.
export const LoyaltyCardsLoader = ({
  isParentLoaded = true,
  children
}: LoaderProps) => {
  const { user } = useSlerp()
  const { setLoyaltyCards, setHasLoyaltyCards, setLoading } = useLoyaltyCards()

  // Not useFetchOnce (doc/ARCHITECTURE.md §6.2's default): it has no
  // fetchPolicy override, and this must stay `no-cache` so a card edited
  // on the Your Cards tab shows up immediately here, not from stale cache.
  const { data, loading } = useQuery(QUERY_LOYALTY_CARDS, {
    variables: { merchant_id: user?.merchant?.id ?? '' },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (data) {
      setLoyaltyCards(data.loyalty_cards ?? [])
      setHasLoyaltyCards(true)
    }
  }, [data, setLoyaltyCards, setHasLoyaltyCards])

  useEffect(() => {
    setLoading(loading)
  }, [loading, setLoading])

  // Not gated on this Loader's own fetch: StampCustomerModal shows an
  // empty select while loading, BulkAddDrawer shows its own Loading state
  // (via `loading` above) — neither needs the page render blocked on it.
  const isLoaded = isParentLoaded

  return <React.Fragment>{children(isLoaded)}</React.Fragment>
}
